"use client";

import type { CapabilitySnapshot } from "@/features/ats-sync/types";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { stageChangeSync, sourcedSyncHintText } from "../lib/gating";

/**
 * Subtle "won't sync" marker shown on synced candidates when the connector
 * can't push stage changes (spec §6). Renders nothing when stage changes sync.
 */
export function WontSyncMarker({
  snapshot,
  ats,
  className,
}: {
  snapshot: CapabilitySnapshot;
  ats: string;
  /** Extra classes on the Badge — lets a host match sibling chips (e.g. the
   * profile header's 26px stage badge). */
  className?: string;
}) {
  const { syncs, reason } = stageChangeSync(snapshot, ats);
  if (syncs) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="warning" size="sm" className={cn("gap-1", className)}>
          <Icon name="IconX" size={12} />
          Won&apos;t sync
        </Badge>
      </TooltipTrigger>
      {reason && <TooltipContent className="max-w-72">{reason}</TooltipContent>}
    </Tooltip>
  );
}

/**
 * Replaces sync affordances for candidates still in Sourced (spec §6) — sync
 * only begins on promotion out of Sourced.
 */
export function SourcedSyncHint({ ats }: { ats: string }) {
  return (
    <p className="text-xs text-contrast/50 leading-4">{sourcedSyncHintText(ats)}</p>
  );
}
